import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ChannelGroupResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

const PrimaryChannelBox = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 16px;
  grid-column: span 2;
`;

const PrimaryChannelDescription = styled(Text)<{ $hasChannels?: boolean }>`
  margin-top: 12px;
  strong {
    font-weight: 700;
  }
  ${({ $hasChannels }) =>
    $hasChannels &&
    css`
      padding-bottom: 16px;
      margin-bottom: 16px;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
    `}
`;

interface ChannelTagProps {
  $isPrimary: boolean;
}

const ChannelTag = styled(Text).attrs({ $type: 'small' })<ChannelTagProps>`
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  height: 24px;
  white-space: nowrap;
  border-radius: 50px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  background-color: ${({ theme }) => theme.colors.white};

  ${({ theme, $isPrimary }) =>
    $isPrimary &&
    css`
      border-color: ${theme.colors.purple};
      color: ${theme.colors.purple};
    `}
`;

const ChannelsHolder = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

interface FormDataT {
  name: string;
  channels: number[];
  primary_channel: null | number;
}

interface Props {
  onClose: () => void;
  editableGroup?: ChannelGroupResource;
}

const GroupForm: FC<Props> = ({ onClose, editableGroup }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { data: channelOptions } = useQuery(
    ['channels', { forChannelGroups: true }],
    () =>
      api.get<
        never,
        Array<{ id: number; name: string; network: 'youtube' | 'facebook' }>
      >(`/v1/distribution/channel-groups/channels`),
    {
      staleTime: 1000 * 60,
      select: (data) =>
        data.map((channel) => ({
          label: `${channel.name} (${channel.network})`,
          value: channel.id,
        })),
    },
  );

  const {
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    control,
    watch,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: editableGroup?.name ?? '',
      channels: editableGroup?.channels?.map((r) => r.id) ?? [],
      primary_channel:
        editableGroup?.channels?.find((c) => c.isPrimary)?.id ?? null,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await (editableGroup
          ? api.put<typeof data, ChannelGroupResource>(
              `/v1/distribution/channel-groups/${editableGroup.id}`,
              data,
            )
          : api.post<typeof data, ChannelGroupResource>(
              `/v1/distribution/channel-groups`,
              data,
            ));

        onClose();

        queryClient.invalidateQueries(['channel_groups'], {
          exact: false,
        });

        alerts.success(
          editableGroup
            ? 'Channel group successfully updated'
            : 'New channel group successfully added',
          data.name,
        );
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  const selectedChannelIds = watch('channels');

  const primaryChannelId = watch('primary_channel');

  const selectedChannels = channelOptions?.filter((o) =>
    selectedChannelIds.includes(o.value),
  );

  useEffect(() => {
    if (!primaryChannelId) {
      return;
    }

    if (selectedChannelIds.includes(primaryChannelId)) {
      return;
    }

    setValue('primary_channel', null);
  }, [selectedChannelIds, primaryChannelId, setValue]);

  return (
    <Modal>
      <SidePanel
        onClose={onClose}
        heading={editableGroup ? 'Edit group' : 'Create group'}
      >
        <SidePanelBody onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                name="name"
                control={control}
                label="Group Name"
                maximumCharacters={38}
              />
              <FormField<FormDataT, number>
                control={control}
                label="Channels"
                placeholder="Add channel"
                name="channels"
                type="multiselect"
                options={channelOptions ?? []}
              />
              <PrimaryChannelBox>
                <Text $type="smallBold">Channel Post Tags</Text>
                <PrimaryChannelDescription
                  $type="smallNormal"
                  $color="gray"
                  $hasChannels={!!selectedChannels?.length}
                >
                  Select channel which should be main when your distribute video
                  as a <strong>'Compilation Type: Fresh'</strong>. Video
                  distributed through other channels will be tagged as{' '}
                  <strong>'Compilation Type: Distribution'</strong>
                </PrimaryChannelDescription>
                <ChannelsHolder>
                  {selectedChannels?.map((c) => (
                    <ChannelTag
                      as="button"
                      key={c.value}
                      type="button"
                      onClick={() => setValue('primary_channel', c.value)}
                      $isPrimary={primaryChannelId === c.value}
                    >
                      {c.label}
                    </ChannelTag>
                  ))}
                </ChannelsHolder>
              </PrimaryChannelBox>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {editableGroup ? 'Update' : 'Create'}
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default GroupForm;
